import { AudioOutlined, SettingOutlined } from "@ant-design/icons";
import { Card, Col, Divider, Flex, Row, Typography, message } from "antd";
import React from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { isPracticeAdminOnly } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import {
  setAITemplateModalState,
  setCreateModalState,
  setEditingState,
  setShowRestrictionModal,
  setShowSettingsModal,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { isSafariDesktop } from "../../utils/device.utils";
import { eventEmitter } from "../../utils/eventEmitter";
import MicTestModal from "./MicTestModal.component";
import RecordFirstPopover from "./RecordFirstPopover.component";
import styles from "./recordcta.module.scss";

export type RecordCTAProps = {
  captureRef?: React.RefObject<HTMLDivElement>;
};

/** The Start card as it was before Quick Start; what users without the Quick Start flag see. */
const ClassicRecordCTA = ({ captureRef }: RecordCTAProps) => {
  const dispatch = useDispatch();

  const componentName = "RecordCTA";

  const { uploadInProgress, selectedClient, isMobilePath, isDemoPath } = useAppSelector((state) => state.appState);
  const { recordingDetail, isRecording, isPaused } = useAppSelector((state) => state.recordings);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { shouldReferAsPatient } = useSpecialityReference();
  const [micTestOpen, setMicTestOpen] = React.useState(false);

  const handleButtonClick = (type: "audio" | "dictate" | "upload" | "write") => {
    //check if it is safari desktop browser if yes we need to show a modal to the user to ask them to use the chrome version or microsoft edge version
    if (isSafariDesktop()) {
      Swal.fire({
        title: "Browser Compatibility",
        text: "Please use Chrome or Microsoft Edge browsers for the best experience. Nudge can no longer support Safari due to browser limitations.",
        icon: "info",
        confirmButtonText: "Got it",
      });
      return;
    }
    if (uploadInProgress) {
      message.warning("Please wait for the current upload to finish.");
      return;
    }
    if (type === "audio" && userInfo.record_first && !isDemoPath) {
      eventEmitter.emit("quickRecord");
      return;
    }
    dispatch(
      setRecordingDetail({
        ...recordingDetail,
        modality_type: type,
        client_id: selectedClient?.id,
      }),
    );
    dispatch(setEditingState(false));
    dispatch(setCreateModalState(true));
  };

  const openAITemplateModal = () => {
    if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setAITemplateModalState(true));
    }
  };

  if (isPracticeAdminOnly(userInfo)) {
    return null;
  }

  return (
    <>
      <Row gutter={[10, 10]} ref={captureRef} className={styles[`${componentName}__container-right-container`]}>
        <Col span={24} className={styles[`${componentName}__container-right-header-container`]}>
          <Typography className={styles[`${componentName}__container-right-header`]}>Generate Note</Typography>
        </Col>
        <Col span={24}>
          <Card
            ref={captureRef}
            className={styles[`${componentName}__container-right-card`]}
            onClick={() => {
              handleButtonClick("audio");
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={styles[`${componentName}__container-right-card-logo`]}
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z"
                  clipRule="evenodd"
                />
              </svg>
              <div
                style={{
                  textAlign: "start",
                }}
              >
                <Typography className={styles[`${componentName}__container-right-card-title`]}>
                  {shouldReferAsPatient ? "Start Encounter" : "Start A Session"}
                </Typography>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={24}>
          <Card
            ref={captureRef}
            className={styles[`${componentName}__container-right-card`]}
            onClick={() => {
              openAITemplateModal();
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <svg
                className={styles[`${componentName}__container-right-card-logo`]}
                width="40"
                height="40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="20" cy="20" r="20" fill="#EEF2FF" />
                <path
                  d="M12.098 27.902a3.752 3.752 0 0 0 2.652 1.099m-2.652-1.099a3.751 3.751 0 0 0 2.652 1.099m-2.652-1.099A3.75 3.75 0 0 1 11 25.25V12.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v4.072m-3.75 12.804a3.75 3.75 0 0 0 2.652-1.099m-2.652 1.099c.995 0 1.949-.396 2.652-1.099m-2.652 1.099L27.875 29c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072m-6.401 6.402 6.401-6.402m-6.401 6.402A3.75 3.75 0 0 0 18.5 25.25v-9.053m5.303 5.303 2.879-2.88c.44-.438.44-1.15 0-1.59l-3.712-3.713a1.124 1.124 0 0 0-1.59 0l-2.88 2.88m-3.75 9.053h.008v.008h-.008v-.008Z"
                  stroke="#6366F1"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div
                style={{
                  textAlign: "start",
                }}
              >
                <Typography className={styles[`${componentName}__container-right-card-title`]}>My Templates</Typography>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={24} className={styles[`${componentName}__container-right-container-bottomcta`]}>
          <Flex align="center" justify="space-between">
            <button
              type="button"
              className={styles[`${componentName}__container-right-container-bottomcta-action`]}
              onClick={() => setMicTestOpen(true)}
              disabled={isRecording || isPaused}
            >
              <AudioOutlined style={{ fontSize: 14 }} />
              Test microphone
            </button>
            <Divider type="vertical" style={{ margin: 0, height: 16 }} />
            <button
              type="button"
              className={styles[`${componentName}__container-right-container-bottomcta-action`]}
              onClick={() => dispatch(setShowSettingsModal(true))}
            >
              <SettingOutlined style={{ fontSize: 14 }} />
              Session settings
            </button>
          </Flex>
        </Col>
        <Col span={24}>
          <RecordFirstPopover />
        </Col>
      </Row>
      <MicTestModal open={micTestOpen} onClose={() => setMicTestOpen(false)} />
    </>
  );
};

export default ClassicRecordCTA;
